/**
 * The artist's login, through Amazon Cognito. The login page takes the email and
 * password itself; only "Forgot password" goes through Cognito's hosted pages.
 * Tokens are kept in this browser so the artist stays logged in on their iPad.
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

const env = import.meta.env
const DOMAIN = (env.VITE_COGNITO_DOMAIN ?? '').replace(/\/+$/, '')
const CLIENT_ID = env.VITE_COGNITO_CLIENT_ID ?? ''
const REGION = env.VITE_AWS_REGION ?? 'us-east-1'
const STORE = 'comic-site-login'
const RETURN_TO = 'comic-site-return-to'

interface Tokens {
  idToken: string
  refreshToken: string
  expiresAt: number
}

const redirectUri = () => `${window.location.origin}/login`

function readTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORE)
    return raw ? (JSON.parse(raw) as Tokens) : null
  } catch {
    return null
  }
}

function writeTokens(tokens: Tokens | null) {
  try {
    if (tokens) localStorage.setItem(STORE, JSON.stringify(tokens))
    else localStorage.removeItem(STORE)
  } catch {
    // Private browsing can block storage; the login then lasts until the page closes.
  }
  memory = tokens
  listeners.forEach((fn) => fn())
}

let memory: Tokens | null = readTokens()
const listeners = new Set<() => void>()

async function tokenRequest(params: Record<string, string>) {
  const res = await fetch(`${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLIENT_ID, ...params }).toString(),
  })
  if (!res.ok) throw new Error('Login failed')
  return res.json() as Promise<{ id_token: string; refresh_token?: string; expires_in: number }>
}

/** Cognito's own API (the one the hosted login page uses), for logging in with a password */
async function cognito<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`https://cognito-idp.${REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}` },
    body: JSON.stringify({ ClientId: CLIENT_ID, ...body }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new LoginError(String(data.__type ?? '').split('#').pop() || 'Unknown')
  return data as T
}

/** Why a password login failed, by Cognito's error name (NotAuthorizedException etc.) */
export class LoginError extends Error {}

interface AuthResult {
  AuthenticationResult?: { IdToken: string; RefreshToken?: string; ExpiresIn: number }
  ChallengeName?: string
  Session?: string
}

function keep(result: AuthResult, refreshToken?: string) {
  const r = result.AuthenticationResult
  if (!r) throw new LoginError(result.ChallengeName ?? 'Unknown')
  writeTokens({ idToken: r.IdToken, refreshToken: r.RefreshToken ?? refreshToken ?? '', expiresAt: Date.now() + r.ExpiresIn * 1000 })
}

/**
 * Log in with email and password. Returns 'done', or 'new-password' when the
 * account still has the temporary password it was created with.
 */
export async function passwordLogin(email: string, password: string): Promise<{ next: 'done' } | { next: 'new-password'; session: string }> {
  const result = await cognito<AuthResult>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    AuthParameters: { USERNAME: email, PASSWORD: password },
  })
  if (result.ChallengeName === 'NEW_PASSWORD_REQUIRED' && result.Session) return { next: 'new-password', session: result.Session }
  keep(result)
  return { next: 'done' }
}

/** Replace the temporary password, which also finishes logging in */
export async function setNewPassword(email: string, password: string, session: string) {
  keep(
    await cognito<AuthResult>('RespondToAuthChallenge', {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      Session: session,
      ChallengeResponses: { USERNAME: email, NEW_PASSWORD: password },
    })
  )
}

/** Cognito's page for resetting a forgotten password; it comes back to /login afterwards */
export function forgotPasswordUrl(): string {
  const params = new URLSearchParams({ client_id: CLIENT_ID, response_type: 'code', scope: 'email openid profile', redirect_uri: redirectUri() })
  return `${DOMAIN}/forgotPassword?${params}`
}

/** Where to go after logging in (the page that sent the artist to /login, if any) */
export function takeReturnTo(): string {
  let next = '/artist/comics'
  try {
    next = sessionStorage.getItem(RETURN_TO) ?? next
    sessionStorage.removeItem(RETURN_TO)
  } catch {
    // ignore
  }
  return next.startsWith('/') ? next : '/artist/comics'
}

let refreshing: Promise<string | null> | null = null

/** A current ID token, refreshed when it's within 5 minutes of expiring */
export async function getIdToken(): Promise<string | null> {
  const t = memory
  if (!t) return null
  if (Date.now() < t.expiresAt - 5 * 60 * 1000) return t.idToken
  // Cognito's API refreshes tokens from both the password login and the hosted one
  refreshing ??= cognito<AuthResult>('InitiateAuth', { AuthFlow: 'REFRESH_TOKEN_AUTH', AuthParameters: { REFRESH_TOKEN: t.refreshToken } })
    .then((result) => {
      keep(result, t.refreshToken)
      return memory?.idToken ?? null
    })
    .catch(() => {
      writeTokens(null)
      return null
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

/** Finish the login on /login?code=...; returns where to go next */
export async function finishLogin(code: string): Promise<string> {
  const data = await tokenRequest({ grant_type: 'authorization_code', code, redirect_uri: redirectUri() })
  writeTokens({ idToken: data.id_token, refreshToken: data.refresh_token ?? '', expiresAt: Date.now() + data.expires_in * 1000 })
  return takeReturnTo()
}

export function logout() {
  writeTokens(null)
  // A password login has no Cognito session to end, only the tokens kept here
  window.location.href = '/'
}

const AuthContext = createContext<{ isArtist: boolean }>({ isArtist: false })

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isArtist, setIsArtist] = useState(Boolean(memory))
  const sync = useCallback(() => setIsArtist(Boolean(memory)), [])
  useEffect(() => {
    listeners.add(sync)
    // Drop an expired login quietly instead of showing artist tools that won't work.
    getIdToken().then(sync)
    return () => {
      listeners.delete(sync)
    }
  }, [sync])
  return <AuthContext.Provider value={{ isArtist }}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
